import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperPlaneRight, Microphone, Stop, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroChat = () => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Initialize Web Speech API
  useEffect(() => {
    // Speech Recognition (STT)
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setMessage(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
        toast.error('Voice recognition error');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Speech Synthesis (TTS)
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Listening... Speak now');
      } catch (error) {
        console.error('Speech recognition error:', error);
        toast.error('Could not start voice recognition');
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const speak = (text) => {
    if (!synthRef.current || !autoSpeak) return;

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synthRef.current.speak(utterance);
  };

  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();

      const federationId = localStorage.getItem('federationId') || '';
      
      const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          persona: 'oracle',
          federation_id: federationId
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('AI service unavailable');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiResponse = '';

      // Add empty AI message that we'll update
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            
            if (data.error) {
              throw new Error(data.error);
            }
            
            if (data.content) {
              aiResponse += data.content;
              // Update the last message
              setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].content = aiResponse;
                return newMessages;
              });
            }
            
            if (data.done) {
              // Speak the complete response
              speak(aiResponse);
              break;
            }
          }
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        toast.info('Request cancelled');
      } else {
        console.error('Chat error:', error);
        toast.error('Oracle is temporarily unavailable');
        // Remove the empty assistant message on error
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="w-full"
    >
      {/* Chat Container */}
      <div className="glass-surface border border-[#00F0FF]/30 rounded-xl p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">💬 Chat with Oracle</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-speak toggle */}
            <button
              onClick={() => {
                setAutoSpeak(!autoSpeak);
                if (autoSpeak) stopSpeaking();
              }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title={autoSpeak ? "Mute responses" : "Enable voice responses"}
            >
              {autoSpeak ? (
                <SpeakerHigh size={20} className="text-[#00F0FF]" />
              ) : (
                <SpeakerSlash size={20} className="text-gray-400" />
              )}
            </button>
            
            {/* Status indicator */}
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ 
                  scale: isSpeaking ? [1, 1.3, 1] : [1, 1.2, 1], 
                  opacity: isSpeaking ? [1, 0.7, 1] : [1, 0.6, 1] 
                }}
                transition={{ duration: isSpeaking ? 0.6 : 2, repeat: Infinity }}
                className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-[#FFB800]' : 'bg-[#00FFA3]'}`}
              />
              <span className="text-sm text-[#00FFA3]">
                {isSpeaking ? 'Speaking' : 'Online'}
              </span>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        {messages.length > 0 && (
          <div className="mb-4 max-h-60 overflow-y-auto space-y-3 custom-scrollbar">
            {messages.map((msg, idx) => (
              <motion.div
                key={`msg-${idx}-${msg.role}-${msg.content.substring(0, 10)}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30 ml-8'
                    : 'bg-[#8A2BE2]/10 border border-[#8A2BE2]/30 mr-8'
                }`}
              >
                <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {msg.content}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Chat Form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ask Oracle anything... or click the mic to speak"
            className="flex-1 px-4 py-3 bg-[#0B0E14] border border-[#00F0FF]/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00F0FF]/60 transition-colors"
            disabled={isLoading || isListening}
          />
          
          {/* Voice Input Button */}
          {recognitionRef.current && (
            <button
              type="button"
              onClick={isListening ? stopListening : startListening}
              disabled={isLoading}
              className={`px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                isListening
                  ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                  : 'bg-[#8A2BE2] hover:bg-[#9D4EDD] text-white'
              }`}
              title={isListening ? "Stop listening" : "Speak your message"}
            >
              {isListening ? (
                <>
                  <Stop size={20} weight="fill" />
                  <span className="hidden sm:inline">Stop</span>
                </>
              ) : (
                <>
                  <Microphone size={20} weight="fill" />
                  <span className="hidden sm:inline">Speak</span>
                </>
              )}
            </button>
          )}

          {/* Send Button */}
          <button
            type="submit"
            disabled={isLoading || !message.trim() || isListening}
            className="px-6 py-3 bg-gradient-to-r from-[#00F0FF] to-[#00D9E8] text-[#030305] font-bold rounded-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-[#030305] border-t-transparent rounded-full"
                />
                <span className="hidden sm:inline">Thinking...</span>
              </>
            ) : (
              <>
                <PaperPlaneRight size={20} weight="fill" />
                <span className="hidden sm:inline">Send</span>
              </>
            )}
          </button>
        </form>

        {/* Quick Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            "Buy Tokens",
            "Truth Gate",
            "Consultation"
          ].map((action) => (
            <button
              key={action}
              onClick={() => setMessage(action)}
              className="px-3 py-1.5 text-xs border border-[#00F0FF]/30 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/10 transition-colors"
            >
              {action}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 240, 255, 0.6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 240, 255, 0.9);
        }
      `}</style>
    </motion.div>
  );
};

export default HeroChat;
